import React, { useState, useRef, useEffect } from "react";
import { aiChatApi } from "../api/aiChatApi";
import { Send, MessageSquareText, X } from "@/assets/icons/icons";

const Chatbot = ({ className = "" }) => {
  // const { socket } = useSocket(); // Socket removed

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: "init-1",
      text: "Hello! I'm your dedicated support assistant. How can I help you with your courses or assignments today?",
      sender: "bot",
    },
  ]);
  const [inputMessage, setInputMessage] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, isOpen]);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputMessage.trim()) return;

    const newMessage = {
      id: Date.now(),
      text: inputMessage,
      sender: "user",
    };

    setMessages((prev) => [...prev, newMessage]);
    const currentInput = inputMessage; // Store for API call

    setInputMessage("");
    setIsTyping(true);

    try {
      const response = await aiChatApi.chat(currentInput);

      if (response.status && response.data?.success) {
        setMessages((prev) => [
          ...prev,
          {
            id: Date.now(),
            text: response.data.data.content,
            sender: "bot",
          },
        ]);
      } else {
        // API handled error, but we might want to show a generic error message in chat if needed
        // For now, just stop typing indicator
        console.error("AI Chat failed:", response.errorMsg);
      }
    } catch (err) {
      console.error("AI Chat error:", err);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <div className={`fixed bottom-6 right-6 z-50 font-sans ${className}`}>
      {/* Toggle Button */}
      <div className={`transition-all duration-300 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}>
        <button
          onClick={() => setIsOpen(true)}
          className="flex items-center justify-center w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-primary text-primary-foreground shadow-2xl hover:scale-105 active:scale-95 transition-all duration-200"
          aria-label="Open support chat"
        >
          <MessageSquareText className="w-6 h-6 sm:w-8 sm:h-8" />
        </button>
      </div>

      {/* Chat Window */}
      <div
        className={`bg-white border border-slate-200 shadow-2xl overflow-hidden flex flex-col transition-all duration-300 origin-bottom-right 
        fixed inset-0 sm:absolute sm:inset-auto sm:bottom-0 sm:right-0 
        w-full h-full sm:w-[380px] sm:h-[600px] sm:max-h-[calc(100vh-100px)]
        rounded-none sm:rounded-2xl
        ${isOpen
            ? 'scale-100 opacity-100 translate-y-0 pointer-events-auto'
            : 'scale-95 opacity-0 translate-y-4 pointer-events-none'
          }`}
      >
        {/* Header */}
        <div className="bg-white p-4 flex items-center justify-between shrink-0 border-b border-slate-100 relative z-10 shadow-sm">
          <div className="flex items-center gap-3">
            <div className="relative">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center border border-slate-100 p-1.5 shadow-sm">
                <img src="/logo.png" alt="Udyog Vriksh" className="w-full h-full object-contain" />
              </div>
              <span className="absolute bottom-0.5 right-0.5 w-2.5 h-2.5 bg-green-500 border-2 border-white rounded-full"></span>
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-sm leading-tight">Udyog Vriksh</h3>
              <p className="text-slate-500 text-[11px] font-medium">Your Learning Assistant</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsOpen(false)}
              className="text-slate-400 hover:text-red-500 p-2 hover:bg-red-50 rounded-full transition-colors"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 bg-slate-50 space-y-6">
          <div className="flex justify-center my-2">
            <span className="bg-slate-200/60 text-slate-500 text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider">
              Today
            </span>
          </div>

          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex flex-col ${msg.sender === 'user' ? 'items-end' : 'items-start'}`}
            >
              {/* Message Bubble */}
              <div className={`flex items-start gap-2 max-w-[85%] ${msg.sender === 'user' ? 'flex-row-reverse' : ''}`}>
                {msg.sender === 'bot' && (
                  <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0 border border-slate-100 p-1 mt-1">
                    <img src="/logo.png" alt="Bot" className="w-full h-full object-contain opacity-80" />
                  </div>
                )}

                <div
                  className={`px-4 py-3 text-sm shadow-sm leading-relaxed ${msg.sender === 'user'
                    ? 'bg-white border border-slate-100 text-slate-800 rounded-2xl rounded-tr-none'
                    : 'bg-primary text-primary-foreground rounded-2xl rounded-tl-none'
                    }`}
                >
                  {msg.text}
                </div>
              </div>
            </div>
          ))}

          {/* Typing Indicator */}
          {isTyping && (
            <div className="flex flex-col items-start px-1">
              <div className="flex items-start gap-2">
                <div className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0 border border-slate-100 p-1 mt-1">
                  <img src="/logo.png" alt="Bot" className="w-full h-full object-contain opacity-80" />
                </div>
                <div className="bg-primary px-4 py-3 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-1.5">
                  <div className="w-1.5 h-1.5 bg-primary-foreground/60 rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-primary-foreground/60 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="w-1.5 h-1.5 bg-primary-foreground/60 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-white border-t border-slate-100 px-3 py-2 z-20">
          <form
            onSubmit={handleSendMessage}
            className="flex items-center gap-1.5 bg-slate-50 border border-slate-200 px-3 py-1 rounded-full focus-within:ring-2 focus-within:ring-primary/10 focus-within:border-primary/20 transition-all shadow-sm"
          >
            <input
              ref={inputRef}
              type="text"
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              placeholder="Type your question here..."
              className="flex-1 bg-transparent py-1.5 text-xs sm:text-sm focus:outline-none placeholder:text-slate-400 text-slate-700"
            />
            <button
              type="submit"
              disabled={!inputMessage.trim()}
              className="p-1.5 bg-primary text-primary-foreground rounded-full hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm active:scale-95 shrink-0"
            >
              <Send size={14} className="translate-x-0.5 translate-y-0.5 mr-0.5" />
            </button>
          </form>

          <div className="pt-2 text-center border-t border-slate-200 mt-2">
            <p className="text-[10px] text-slate-500 font-medium hover:text-slate-600 transition-colors cursor-default">
              Powered by Udyog Vriksh AI
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chatbot;
